import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { environment } from "../../../environments/environment";

interface CacheEntry {
  data: BehaviorSubject<HttpResponse<any>>;
  expiration: number;
}

@Injectable()
export class WSInterceptor implements HttpInterceptor {
  private cache: Map<string, CacheEntry> = new Map();
  // private cacheDuration = 30000; // Duração do cache em milissegundos (30s)
  private cacheDuration = 0; // Duração do cache em milissegundos (30s)
  private cacheSizeLimit = 30; // Limite de tamanho do cache

  constructor(
    private translateService: TranslateService,
    private cookieService: CookieService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let modifiedReq = req;

    // Adiciona a reescrita de URL para ambiente de desenvolvimento
    const baseUrl = environment.urlbase;
    if (!environment.production && modifiedReq.url.startsWith(baseUrl)) {
      try {
        const urlObj = new URL(modifiedReq.url);
        modifiedReq = modifiedReq.clone({
            url: `${urlObj.pathname}${urlObj.search}`
        });
      } catch (e) {
        // Ignora erros de parsing de URL
      }
    }

    const browserLang = this.translateService.getBrowserCultureLang();
    const isMobileViewport =
      typeof window !== "undefined" && window.innerWidth <= 576;
    const clientDeviceType = isMobileViewport ? "mobile" : "desktop";
    modifiedReq = modifiedReq.clone({
      setHeaders: {
        "Accept-Language": browserLang,
        "X-Client-Device": clientDeviceType,
      },
      withCredentials: true,
    });

    const cookieName = 'XSRF-TOKEN';
    const headerName = 'X-XSRF-TOKEN';
    const token = this.getCookie(cookieName);

    if (token && !modifiedReq.headers.has(headerName)) {
      modifiedReq = modifiedReq.clone({
        headers: modifiedReq.headers.set(headerName, token),
      });
    }

    if (modifiedReq.method === 'DELETE') {
      modifiedReq = modifiedReq.clone({
        body: {},
      });
    }

    if (modifiedReq.method === 'GET' && modifiedReq.url.includes('app/posts')) {
      const cacheKey = modifiedReq.urlWithParams;
      const cacheEntry = this.cache.get(cacheKey);
      const now = Date.now();

      const forceParam = modifiedReq.params.get('force');

      if (forceParam === 'true') {
        return next.handle(modifiedReq);
      }

      if (cacheEntry && cacheEntry.expiration > now) {
        return cacheEntry.data.asObservable();
      }

      const newCacheEntry: CacheEntry = {
        data: new BehaviorSubject<HttpResponse<any>>(null),
        expiration: now + this.cacheDuration
      };

      this.cache.set(cacheKey, newCacheEntry);

      return next.handle(modifiedReq).pipe(
        tap(event => {
          if (event instanceof HttpResponse) {
            newCacheEntry.data.next(event);
            this.ensureCacheSizeLimit();
          }
        })
      );
    }

    return next.handle(modifiedReq);
  }

  private ensureCacheSizeLimit() {
    const now = Date.now();

    // Remove todos elementos já expirados
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expiration <= now) {
        this.cache.delete(key);
      }
    }

    // Se ainda o tamanho do cache for maior que o limite, remove os elementos mais antigos
    while (this.cache.size > this.cacheSizeLimit) {
      const oldestKey = Array.from(this.cache.keys())[0];
      this.cache.delete(oldestKey);
    }
  }

  private getCookie(name: string): string {
    return this.cookieService.get(name);
  }
}
